/**
 * Página de Asuntos Propios (A.P.) para usuarios
 * Muestra balance, solicitudes, historial y permite configurar arrastre
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ap as apApi, user as userApi, motivos as motivosApi } from '../services/api';
import { useToast } from '../context/ToastContext';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { getTurnoForDate } from '../utils/dateUtils';
import {
    ArrowLeft,
    Calendar,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    Plus,
    Trash2,
    ChevronLeft,
    ChevronRight,
    FileText,
    History,
    Settings,
    Download,
    Check,
    X,
    HelpCircle
} from 'lucide-react';
import OnboardingTour from '../components/OnboardingTour';

// Generate days for a month
const generateMonthDays = (year, month) => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    const startOffset = firstDay === 0 ? 6 : firstDay - 1;

    const days = [];
    for (let i = 0; i < startOffset; i++) {
        days.push({ empty: true, key: `empty-${i}` });
    }
    for (let day = 1; day <= daysInMonth; day++) {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        days.push({ day, dateStr, key: dateStr });
    }
    return days;
};

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const WEEKDAYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

// Onboarding tour steps for AP section
const AP_TOUR_STEPS = [
    {
        target: '#tour-ap-header',
        title: '¿Qué son los Asuntos Propios?',
        content: 'Aquí gestionas tus días de A.P. Cada año tienes 6 días que puedes usar para asuntos personales.'
    },
    {
        target: '#tour-ap-balance',
        title: 'Tu Balance',
        content: 'Estos números muestran cuántos días tienes disponibles, cuántos has usado y cuántos están pendientes de respuesta.'
    },
    {
        target: '#tour-ap-settings',
        title: 'Días de Arrastre',
        content: 'Si no usaste todos los días del año anterior, los puedes "arrastrar". Pulsa el engranaje para configurarlos.'
    },
    // Modal se abre automáticamente en el paso 4 (index 3)
    {
        target: '#tour-ap-modal-input',
        title: 'Cuántos días arrastras',
        content: 'Introduce aquí los días de A.P. que no usaste el año pasado (normalmente los denegados).'
    },
    {
        target: '#tour-ap-modal-rule',
        title: '⚠️ Regla del Bonus',
        content: 'Los primeros 3 días valen 1 día cada uno. ¡A partir del 4º, cada día vale 2! Es una ventaja importante.'
    },
    {
        target: '#tour-ap-modal-save',
        title: 'Guardar Arrastre',
        content: 'Pulsa Guardar cuando hayas introducido los días. Puedes modificarlo cuando quieras.'
    },
    // Al pasar al paso 7 (index 6), el modal se cierra automáticamente
    {
        target: '#tour-ap-new',
        title: 'Pedir un Día',
        content: 'Pulsa aquí para solicitar un día de A.P. La empresa te responderá si lo concede o no.'
    }
];

export default function AsuntosPropios() {
    const navigate = useNavigate();
    const toast = useToast();

    const [loading, setLoading] = useState(true);
    // Use localStorage or 2026 as default
    const [currentYear, setCurrentYear] = useState(() => {
        // Use the year from the main calendar if available, otherwise current year
        const saved = sessionStorage.getItem('ap_view_year');
        return saved !== null ? parseInt(saved) : new Date().getFullYear();
    });
    const [balance, setBalance] = useState(null);
    const [solicitudes, setSolicitudes] = useState([]);
    const [historial, setHistorial] = useState([]);
    const [activeTab, setActiveTab] = useState('solicitudes');
    const [calendarData, setCalendarData] = useState(null);
    const [conductor, setConductor] = useState(null);
    const [diasConMotivo, setDiasConMotivo] = useState([]);

    // For new solicitud - picker syncs with currentYear when opened
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [pickerMonth, setPickerMonth] = useState(() => new Date().getMonth());
    const [pickerYear, setPickerYear] = useState(() => new Date().getFullYear());

    // For type selection modal (after selecting date)
    const [showTypeSelector, setShowTypeSelector] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);

    // For arrastre config
    const [showArrastreConfig, setShowArrastreConfig] = useState(false);
    const [arrastreDias, setArrastreDias] = useState(0);
    const [arrastreNotas, setArrastreNotas] = useState('');

    // For cancel confirmation
    const [cancelConfirmId, setCancelConfirmId] = useState(null);
    const [cancelling, setCancelling] = useState(false);

    // For marking estado
    const [markingEstado, setMarkingEstado] = useState(null);

    // For onboarding modal (first time users)
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [onboardingStep, setOnboardingStep] = useState(1);
    const [hasArrastreAnterior, setHasArrastreAnterior] = useState(null);

    // Onboarding tour state
    const [showTour, setShowTour] = useState(false);
    const [tourCurrentStep, setTourCurrentStep] = useState(0);

    // Show tour on first visit (separate from arrastre onboarding modal)
    useEffect(() => {
        const hasSeenTour = localStorage.getItem('onboarding_ap_seen');
        if (!hasSeenTour && !loading) {
            const timer = setTimeout(() => setShowTour(true), 1000);
            return () => clearTimeout(timer);
        }
    }, [loading]);

    const handleTourComplete = () => {
        setShowTour(false);
        localStorage.setItem('onboarding_ap_seen', 'true');
    };

    const handleTourSkip = () => {
        setShowTour(false);
        localStorage.setItem('onboarding_ap_seen', 'true');
    };

    const restartTour = () => {
        setTourCurrentStep(0);
        setShowTour(true);
    };

    const handleTourStepChange = (stepIndex) => {
        setTourCurrentStep(stepIndex);
        // Auto-open modal on step 4 (index 3), close on step 7 (index 6)
        if (stepIndex === 3) {
            setShowArrastreConfig(true);
        } else if (stepIndex >= 6) {
            setShowArrastreConfig(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [currentYear]);

    useEffect(() => {
        loadCalendar(pickerYear);
    }, [pickerYear]);

    // Save year to sessionStorage (resets on new session, initializes to current year)
    useEffect(() => {
        sessionStorage.setItem('ap_view_year', currentYear.toString());
    }, [currentYear]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [balanceData, solicitudesData, historialData, conductorData] = await Promise.all([
                apApi.getBalance(currentYear),
                apApi.getSolicitudes(currentYear),
                apApi.getHistorial(currentYear),
                userApi.getConductor()
            ]);
            setBalance(balanceData);
            setSolicitudes(solicitudesData);
            setHistorial(historialData);
            setConductor(conductorData);
            if (balanceData) {
                setArrastreDias(balanceData.arrastre_dias || 0);
                setArrastreNotas(balanceData.arrastre_notas || '');
            }

            // Check if this is first time access (no balance configured and no solicitudes)
            const onboardingKey = `ap_onboarding_${currentYear}`;
            const hasSeenOnboarding = localStorage.getItem(onboardingKey);
            const isFirstTime = !hasSeenOnboarding &&
                balanceData?.arrastre_dias === 0 &&
                solicitudesData?.length === 0 &&
                historialData?.length === 0;

            if (isFirstTime) {
                setShowOnboarding(true);
            }
        } catch (error) {
            console.error('Error loading AP data:', error);
            toast.error('Error al cargar datos de A.P.');
        } finally {
            setLoading(false);
        }
    };

    const loadCalendar = async (year) => {
        try {
            const data = await userApi.getCalendario(year);
            setCalendarData(data);

            // Load motivos data if conductor is available
            if (data.conductor?.id) {
                const motivosData = await motivosApi.getDias(data.conductor.id, year);
                setDiasConMotivo(motivosData);
            }
        } catch (error) {
            console.error('Error loading calendar data:', error);
        }
    };

    // Called when user selects a date - now shows type selector first
    const handleDateSelected = (fecha) => {
        setSelectedDate(fecha);
        setShowDatePicker(false);
        setShowTypeSelector(true);
    };

    // Called when user selects type (AP or Ajuste Jornada)
    const handleSolicitar = async (tipo) => {
        if (!selectedDate) return;
        try {
            await apApi.solicitar(selectedDate, tipo);
            const mensaje = tipo === 'ajuste_jornada'
                ? 'Solicitud de Ajuste de Jornada creada'
                : 'Solicitud de A.P. creada';
            toast.success(mensaje);
            setShowTypeSelector(false);
            setSelectedDate(null);
            loadData();
        } catch (error) {
            toast.error(error.message || 'Error al crear solicitud');
        }
    };

    const handleCancelar = async (id) => {
        setCancelling(true);
        try {
            await apApi.cancelar(id);
            toast.success('Solicitud cancelada');
            setCancelConfirmId(null);
            loadData();
        } catch (error) {
            toast.error(error.message || 'Error al cancelar');
        } finally {
            setCancelling(false);
        }
    };

    const handleSaveArrastre = async () => {
        try {
            await apApi.updateBalance({
                ano: currentYear,
                arrastre_dias: arrastreDias,
                notas: arrastreNotas
            });
            toast.success('Arrastre actualizado');
            setShowArrastreConfig(false);
            loadData();
        } catch (error) {
            toast.error(error.message || 'Error al guardar');
        }
    };

    // Mark a solicitud as concedido or denegado (after company response)
    const handleMarcarEstado = async (id, estado) => {
        setMarkingEstado(id);
        try {
            await apApi.marcarEstado(id, estado);
            const mensaje = estado === 'concedido'
                ? '✓ Día marcado como CONCEDIDO'
                : '✗ Día marcado como DENEGADO (añadido al arrastre del próximo año)';
            toast.success(mensaje);
            loadData();
        } catch (error) {
            toast.error(error.message || 'Error al marcar estado');
        } finally {
            setMarkingEstado(null);
        }
    };

    // Download PDF summary
    const handleDownloadPdf = async () => {
        toast.info('Generando PDF... (funcionalidad próximamente)');
        // TODO: Implement PDF generation
    };

    // Complete onboarding and save initial arrastre if any
    const handleOnboardingComplete = async () => {
        try {
            if (hasArrastreAnterior && arrastreDias > 0) {
                await apApi.updateBalance({
                    ano: currentYear,
                    arrastre_dias: arrastreDias,
                    notas: arrastreNotas || `Arrastre inicial configurado el ${new Date().toLocaleDateString('es-ES')}`
                });
                toast.success(`Configurado arrastre de ${arrastreDias} días`);
            }
            // Mark onboarding as completed for this year
            localStorage.setItem(`ap_onboarding_${currentYear}`, 'true');
            setShowOnboarding(false);
            setOnboardingStep(1);
            setHasArrastreAnterior(null);
            loadData();
        } catch (error) {
            toast.error(error.message || 'Error al guardar configuración inicial');
        }
    };

    // Skip onboarding (no arrastre)
    const handleSkipOnboarding = () => {
        localStorage.setItem(`ap_onboarding_${currentYear}`, 'true');
        setShowOnboarding(false);
        setOnboardingStep(1);
        setHasArrastreAnterior(null);
    };

    // Check if date already has solicitud
    const getSolicitudForDate = (dateStr) => {
        return solicitudes.find(s => s.fecha?.split('T')[0] === dateStr);
    };

    // Estado badge component
    const EstadoBadge = ({ estado }) => {
        const styles = {
            pendiente: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            concedido: 'bg-green-500/20 text-green-400 border-green-500/30',
            denegado: 'bg-red-500/20 text-red-400 border-red-500/30'
        };
        const icons = {
            pendiente: <Clock className="w-3 h-3" />,
            concedido: <CheckCircle className="w-3 h-3" />,
            denegado: <XCircle className="w-3 h-3" />
        };
        const labels = {
            pendiente: 'Pendiente',
            concedido: 'Concedido',
            denegado: 'Denegado'
        };

        return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${styles[estado]}`}>
                {icons[estado]}
                {labels[estado]}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="w-8 h-8 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-0 pb-24">
            {/* Header */}
            <div id="tour-ap-header" className="bg-gradient-to-r from-orange-600 to-amber-600 px-4 py-4 sm:py-6 rounded-b-3xl shadow-lg relative mb-6">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/user')}
                        className="bg-white/20 hover:bg-white/30 backdrop-blur p-2 rounded-full transition-colors text-white"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex-1">
                        <h1 className="text-xl sm:text-2xl font-black text-white leading-tight">Asuntos Propios</h1>
                        <p className="text-xs sm:text-sm text-orange-100 font-medium">Gestiona tus días de A.P.</p>
                    </div>
                    <button
                        onClick={restartTour}
                        className="bg-white/20 hover:bg-white/30 backdrop-blur p-2 rounded-full transition-colors text-white"
                        title="Ver ayuda"
                    >
                        <HelpCircle className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setCurrentYear(y => y - 1)}
                            className="bg-white/20 hover:bg-white/30 backdrop-blur p-2 rounded-full transition-colors text-white"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="px-2 sm:px-3 py-1 bg-white/20 rounded-lg text-sm sm:text-base font-black text-white">{currentYear}</span>
                        <button
                            onClick={() => setCurrentYear(y => y + 1)}
                            className="bg-white/20 hover:bg-white/30 backdrop-blur p-2 rounded-full transition-colors text-white"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content Container */}
            <div className="px-4 sm:px-6 space-y-4">
                {/* Balance Card */}
                {balance && (
                    <div id="tour-ap-balance" className="bg-[var(--bg-secondary)] rounded-2xl p-4 border border-[var(--glass-border)] shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-semibold flex items-center gap-2 text-[var(--text-primary)]">
                            <Calendar className="w-5 h-5 text-orange-500" />
                            Balance {currentYear}
                        </h2>
                        <button
                            id="tour-ap-settings"
                            onClick={() => setShowArrastreConfig(true)}
                            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--glass-bg)] rounded-lg"
                            title="Configurar arrastre"
                        >
                            <Settings className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-3 gap-1.5 sm:gap-3 text-center mb-2">
                        <div className="bg-[var(--bg-primary)] rounded-xl p-2 sm:p-3 shadow-sm border border-[var(--glass-border)] flex flex-col justify-center min-w-0">
                            <p className="text-lg sm:text-2xl font-black text-orange-500 truncate">{balance.disponibles}</p>
                            <p className="text-[9px] sm:text-[10px] md:text-xs font-black text-[var(--text-muted)] mt-1 uppercase tracking-tighter truncate">Disponibles</p>
                        </div>
                        <div className="bg-[var(--bg-primary)] rounded-xl p-2 sm:p-3 shadow-sm border border-[var(--glass-border)] flex flex-col justify-center min-w-0">
                            <p className="text-lg sm:text-2xl font-black text-cyan-500 truncate">{balance.usados}</p>
                            <p className="text-[9px] sm:text-[10px] md:text-xs font-black text-[var(--text-muted)] mt-1 uppercase tracking-tighter truncate">Usados</p>
                        </div>
                        <div className="bg-[var(--bg-primary)] rounded-xl p-2 sm:p-3 shadow-sm border border-[var(--glass-border)] flex flex-col justify-center min-w-0">
                            <p className="text-lg sm:text-2xl font-black text-amber-500 truncate">{balance.pendientes}</p>
                            <p className="text-[9px] sm:text-[10px] md:text-xs font-black text-[var(--text-muted)] mt-1 uppercase tracking-tighter truncate">Pendientes</p>
                        </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-[var(--glass-border)] text-sm text-[var(--text-muted)] px-2">
                        <div className="flex justify-between mb-1">
                            <span className="font-medium">Cuota base:</span>
                            <span className="font-bold">{balance.cuota_base} días</span>
                        </div>
                        {balance.arrastre_equivalente > 0 && (
                            <div id="tour-ap-arrastre" className="flex justify-between text-orange-500 font-medium mb-1">
                                <span>Arrastre ({balance.arrastre_dias} días):</span>
                                <span>+{balance.arrastre_equivalente} días</span>
                            </div>
                        )}
                        <div className="flex justify-between font-bold text-[var(--text-primary)]">
                            <span>Total:</span>
                            <span>{balance.cuota_total} días</span>
                        </div>
                        {balance.ultimo_trimestre && (
                            <div className="mt-2 text-xs">
                                <span className="text-amber-400">
                                    ⚠️ Oct-Dic: {balance.ultimo_trimestre.usados}/{balance.ultimo_trimestre.max} usados
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Tabs */}
                <div className="flex gap-2 py-1">
                <button
                    onClick={() => setActiveTab('solicitudes')}
                    className={`flex-1 flex justify-center items-center gap-2 px-2 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all ${activeTab === 'solicitudes'
                        ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md shadow-orange-500/20'
                        : 'bg-[var(--glass-bg)] text-[var(--text-muted)] border border-[var(--glass-border)] hover:bg-[var(--glass-border)]'
                        }`}
                >
                    <FileText className="w-4 h-4" />
                    Solicitudes
                </button>
                <button
                    onClick={() => setActiveTab('historial')}
                    className={`flex-1 flex justify-center items-center gap-2 px-2 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all ${activeTab === 'historial'
                        ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md shadow-orange-500/20'
                        : 'bg-[var(--glass-bg)] text-[var(--text-muted)] border border-[var(--glass-border)] hover:bg-[var(--glass-border)]'
                        }`}
                >
                    <History className="w-4 h-4" />
                    Historial
                </button>
            </div>

            {/* Solicitudes Tab */}
            {activeTab === 'solicitudes' && (
                <div className="space-y-3">
                    {/* Nueva solicitud button */}
                    <button
                        id="tour-ap-new"
                        onClick={() => {
                            setPickerYear(currentYear);
                            setPickerMonth(new Date().getMonth());
                            setShowDatePicker(true);
                        }}
                        className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-orange-500/40 text-orange-500 hover:bg-orange-500/10 transition-all font-bold"
                    >
                        <Plus className="w-5 h-5" />
                        Nueva Solicitud
                    </button>

                    {solicitudes.length === 0 ? (
                        <div className="text-center py-8 text-[var(--text-muted)]">
                            <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>No hay solicitudes para {currentYear}</p>
                        </div>
                    ) : (
                        solicitudes.map(s => (
                            <div
                                key={s.id}
                                className="bg-[var(--glass-bg)] rounded-xl p-4 border border-[var(--glass-border)] shadow-sm"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1">
                                        <p className="font-bold text-[var(--text-primary)]">
                                            {format(parseISO(s.fecha), 'EEEE, d MMMM', { locale: es })}
                                        </p>
                                        <p className="text-xs font-medium text-[var(--text-muted)] mt-1">
                                            Solicitado {format(parseISO(s.fecha_solicitud), 'd MMM yyyy', { locale: es })}
                                        </p>
                                        {s.fecha_respuesta && (
                                            <p className="text-xs font-medium text-[var(--text-muted)] mt-0.5">
                                                Respuesta: {format(parseISO(s.fecha_respuesta), 'd MMM yyyy', { locale: es })}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        {/* Tipo badge */}
                                        {s.tipo === 'ajuste_jornada' ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-500/20 text-orange-400 border border-orange-500/30">
                                                🔄 Ajuste
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-500/10 text-orange-500 border border-orange-500/20">
                                                📋 A.P.
                                            </span>
                                        )}
                                        <EstadoBadge estado={s.estado} />
                                    </div>
                                </div>

                                {/* Action buttons for pending requests */}
                                {s.estado === 'pendiente' && (
                                    <div className="mt-3 pt-3 border-t border-[var(--glass-border)]">
                                        <p className="text-xs font-bold text-[var(--text-muted)] mb-2 uppercase tracking-wider">¿Respuesta de la empresa?</p>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleMarcarEstado(s.id, 'concedido')}
                                                disabled={markingEstado === s.id}
                                                className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-green-500/20 text-green-400 border border-green-500/30 rounded-lg hover:bg-green-500/30 transition-all disabled:opacity-50"
                                            >
                                                {markingEstado === s.id ? (
                                                    <div className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                                                ) : (
                                                    <Check className="w-4 h-4" />
                                                )}
                                                Concedido
                                            </button>
                                            <button
                                                onClick={() => handleMarcarEstado(s.id, 'denegado')}
                                                disabled={markingEstado === s.id}
                                                className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/30 transition-all disabled:opacity-50"
                                            >
                                                {markingEstado === s.id ? (
                                                    <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                                                ) : (
                                                    <X className="w-4 h-4" />
                                                )}
                                                Denegado
                                            </button>
                                            <button
                                                onClick={() => setCancelConfirmId(s.id)}
                                                className="p-2 text-[var(--text-muted)] hover:text-red-500 hover:bg-[var(--glass-bg)] rounded-lg transition-colors"
                                                title="Cancelar solicitud"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Historial Tab */}
            {activeTab === 'historial' && (
                <div className="space-y-3">
                    {historial.length === 0 ? (
                        <div className="text-center py-8 text-[var(--text-muted)]">
                            <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>Sin historial para {currentYear}</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {historial.map(h => (
                                <div key={h.id} className="flex gap-3 p-3 bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-xl">
                                    <div className={`w-2 h-2 mt-2 rounded-full flex-shrink-0 ${h.estado_nuevo === 'pendiente' ? 'bg-yellow-400' :
                                        h.estado_nuevo === 'concedido' ? 'bg-emerald-400' : 'bg-red-400'
                                        }`} />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm">
                                            <span className="font-bold text-[var(--text-primary)]">
                                                {format(parseISO(h.solicitud_fecha), 'd MMM', { locale: es })}
                                            </span>
                                            <span className="text-[var(--text-muted)] mx-1"> → </span>
                                            <span className={`font-bold uppercase tracking-wider text-[10px] ${
                                                h.estado_nuevo === 'concedido' ? 'text-emerald-500' :
                                                    h.estado_nuevo === 'denegado' ? 'text-red-500' : 'text-yellow-500'
                                            }`}>
                                                {h.estado_nuevo}
                                            </span>
                                        </p>
                                        <p className="text-xs font-medium text-[var(--text-muted)] mt-1">
                                            {format(parseISO(h.fecha), 'd MMM yyyy HH:mm', { locale: es })}
                                            {h.usuario_nombre && ` por ${h.usuario_nombre}`}
                                        </p>
                                        {h.notas && (
                                            <p className="text-xs text-[var(--text-muted)] mt-1 italic opacity-80">"{h.notas}"</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Date Picker Modal */}
            {showDatePicker && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-900 rounded-2xl p-4 w-full max-w-sm border border-white/10">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold">Selecciona fecha</h3>
                            <button
                                onClick={() => setShowDatePicker(false)}
                                className="p-2 text-[var(--text-muted)] hover:text-white"
                            >
                                <XCircle className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Month navigation */}
                        <div className="flex items-center justify-between mb-4">
                            <button
                                onClick={() => {
                                    if (pickerMonth === 0) {
                                        setPickerMonth(11);
                                        setPickerYear(y => y - 1);
                                    } else {
                                        setPickerMonth(m => m - 1);
                                    }
                                }}
                                className="p-2 hover:bg-white/10 rounded-lg"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <span className="font-medium">{MONTHS[pickerMonth]} {pickerYear}</span>
                            <button
                                onClick={() => {
                                    if (pickerMonth === 11) {
                                        setPickerMonth(0);
                                        setPickerYear(y => y + 1);
                                    } else {
                                        setPickerMonth(m => m + 1);
                                    }
                                }}
                                className="p-2 hover:bg-white/10 rounded-lg"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Calendar grid */}
                        <div className="grid grid-cols-7 gap-1 text-center text-xs mb-2">
                            {WEEKDAYS.map(d => (
                                <div key={d} className="py-1 text-[var(--text-secondary)]">{d}</div>
                            ))}
                        </div>
                        <div className="grid grid-cols-7 gap-1">
                            {generateMonthDays(pickerYear, pickerMonth).map(d => {
                                if (d.empty) {
                                    return <div key={d.key} />;
                                }
                                const existing = getSolicitudForDate(d.dateStr);
                                const isPast = new Date(d.dateStr) < new Date().setHours(0, 0, 0, 0);

                                // Check if it's a work day
                                let isWorkDay = true;
                                if (calendarData) {
                                    const isLibranza = calendarData.libranzas?.includes(d.dateStr);
                                    const isVacacion = calendarData.vacaciones?.includes(d.dateStr);
                                    const isAnexo = calendarData.libranzasAnexo?.includes(d.dateStr);

                                    // Handle festivos (including partial)
                                    const festivo = calendarData.festivos?.find(f => f.fecha === d.dateStr);
                                    let isFestivoEffective = false;

                                    if (festivo) {
                                        if (festivo.turnoAfectado === 'ambos' || !festivo.turnoAfectado) {
                                            isFestivoEffective = true;
                                        } else {
                                            // Partial holiday - check if it matches conductor shift
                                            const shiftOverride = calendarData.shiftOverrides?.[d.dateStr];
                                            const currentShift = shiftOverride || getTurnoForDate(d.dateStr, conductor?.turno);
                                            if (currentShift === festivo.turnoAfectado) {
                                                isFestivoEffective = true;
                                            }
                                        }
                                    }

                                    // Check for ANY custom motivo (manual change, shift override, etc)
                                    const hasMotivo = diasConMotivo.some(m => m.fecha?.split('T')[0] === d.dateStr);

                                    if (isLibranza || isVacacion || isAnexo || isFestivoEffective || hasMotivo) {
                                        isWorkDay = false;
                                    }
                                }

                                const isDisabled = existing || isPast || !isWorkDay;

                                return (
                                    <button
                                        key={d.key}
                                        onClick={() => !isDisabled && handleDateSelected(d.dateStr)}
                                        disabled={isDisabled}
                                        className={`p-2 rounded-lg text-sm transition-all ${existing?.estado === 'concedido' ? 'bg-green-500/30 text-green-400' :
                                            existing?.estado === 'pendiente' ? 'bg-yellow-500/30 text-yellow-400' :
                                                existing?.estado === 'denegado' ? 'bg-red-500/30 text-red-400 line-through' :
                                                    (isPast || !isWorkDay) ? 'text-gray-600 cursor-not-allowed opacity-50' :
                                                        'hover:bg-green-500/20 text-white'
                                            }`}
                                    >
                                        {d.day}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-4 flex gap-2 text-xs text-[var(--text-secondary)]">
                            <span className="flex items-center gap-1">
                                <div className="w-3 h-3 rounded bg-green-500/30" /> Concedido
                            </span>
                            <span className="flex items-center gap-1">
                                <div className="w-3 h-3 rounded bg-yellow-500/30" /> Pendiente
                            </span>
                            <span className="flex items-center gap-1">
                                <div className="w-3 h-3 rounded bg-red-500/30" /> Denegado
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Type Selection Modal - shown after date selection */}
            {showTypeSelector && selectedDate && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-900 rounded-2xl p-5 w-full max-w-sm border border-white/10">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-lg">¿Qué tipo de día solicitas?</h3>
                            <button
                                onClick={() => {
                                    setShowTypeSelector(false);
                                    setSelectedDate(null);
                                }}
                                className="p-2 text-[var(--text-muted)] hover:text-white"
                            >
                                <XCircle className="w-5 h-5" />
                            </button>
                        </div>

                        <p className="text-sm text-[var(--text-muted)] mb-4">
                            Fecha seleccionada: <span className="text-white font-medium">{selectedDate}</span>
                        </p>

                        <div className="space-y-3">
                            {/* A.P. Option */}
                            <button
                                onClick={() => handleSolicitar('ap')}
                                disabled={balance?.desglose?.ap?.disponibles <= 0}
                                className="w-full p-4 rounded-xl border-2 border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/20 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <div className="flex items-center gap-3 mb-2">
                                    <span className="text-2xl">📋</span>
                                    <div>
                                        <h4 className="font-semibold text-cyan-400">Asunto Propio (A.P.)</h4>
                                        <p className="text-xs text-[var(--text-muted)]">
                                            {balance?.desglose?.ap?.disponibles || 0} de {balance?.cuota_base || 6} disponibles
                                        </p>
                                    </div>
                                </div>
                                <p className="text-xs text-[var(--text-secondary)]">
                                    La empresa puede conceder o denegar esta solicitud.
                                </p>
                            </button>

                            {/* Ajuste de Jornada Option */}
                            <button
                                onClick={() => handleSolicitar('ajuste_jornada')}
                                disabled={balance?.desglose?.ajuste_jornada?.disponibles <= 0}
                                className="w-full p-4 rounded-xl border-2 border-orange-500/30 bg-orange-500/10 hover:bg-orange-500/20 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <div className="flex items-center gap-3 mb-2">
                                    <span className="text-2xl">🔄</span>
                                    <div>
                                        <h4 className="font-semibold text-orange-400">Ajuste de Jornada</h4>
                                        <p className="text-xs text-[var(--text-muted)]">
                                            {balance?.desglose?.ajuste_jornada?.disponibles || 0} días de arrastre disponibles
                                        </p>
                                    </div>
                                </div>
                                <p className="text-xs text-[var(--text-secondary)]">
                                    Días de arrastre del año anterior. La empresa <span className="text-orange-400 font-medium">debe</span> concederlos.
                                </p>
                            </button>
                        </div>

                        {balance?.arrastre_equivalente === 0 && (
                            <p className="mt-4 text-xs text-center text-[var(--text-secondary)]">
                                ¿Tienes días de arrastre? <button onClick={() => { setShowTypeSelector(false); setShowArrastreConfig(true); }} className="text-cyan-400 underline">Configúralos aquí</button>
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Arrastre Config Modal */}
            {showArrastreConfig && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-900 rounded-2xl p-4 w-full max-w-sm border border-white/10">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold">Configurar Arrastre</h3>
                            <button
                                onClick={() => setShowArrastreConfig(false)}
                                className="p-2 text-[var(--text-muted)] hover:text-white"
                            >
                                <XCircle className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div id="tour-ap-modal-input">
                                <label className="block text-sm text-[var(--text-muted)] mb-1">
                                    Días arrastrados del año anterior
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    max="10"
                                    value={arrastreDias === 0 ? '' : arrastreDias}
                                    onChange={(e) => setArrastreDias(e.target.value === '' ? 0 : parseInt(e.target.value) || 0)}
                                    onFocus={(e) => e.target.select()}
                                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                                />
                                <p id="tour-ap-modal-rule" className="text-xs text-[var(--text-secondary)] mt-1">
                                    Regla: 1-3 días = 1 día c/u, 4º+ = 2 días c/u
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm text-[var(--text-muted)] mb-1">
                                    Notas (opcional)
                                </label>
                                <textarea
                                    value={arrastreNotas}
                                    onChange={(e) => setArrastreNotas(e.target.value)}
                                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg h-20 resize-none"
                                    placeholder="Ej: APs denegados de 2024..."
                                />
                            </div>

                            {arrastreDias > 0 && (
                                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 text-sm">
                                    <p className="text-orange-400 font-medium">
                                        {arrastreDias} días → {
                                            (() => {
                                                let eq = 0;
                                                for (let i = 1; i <= arrastreDias; i++) {
                                                    eq += (i >= 4) ? 2 : 1;
                                                }
                                                return eq;
                                            })()
                                        } días equivalentes
                                    </p>
                                </div>
                            )}

                            <button
                                id="tour-ap-modal-save"
                                onClick={handleSaveArrastre}
                                className="w-full py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-bold shadow-lg shadow-orange-500/20"
                            >
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cancel Confirmation Modal */}
            {cancelConfirmId && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-900 rounded-2xl p-5 w-full max-w-sm border border-white/10">
                        <h3 className="font-semibold text-lg mb-2">¿Cancelar solicitud?</h3>
                        <p className="text-sm text-[var(--text-muted)] mb-6">
                            Esta acción eliminará tu solicitud de A.P. pendiente.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setCancelConfirmId(null)}
                                className="flex-1 py-3 rounded-xl border border-white/20 text-gray-300 hover:bg-white/5"
                                disabled={cancelling}
                            >
                                No, mantener
                            </button>
                            <button
                                onClick={() => handleCancelar(cancelConfirmId)}
                                disabled={cancelling}
                                className="flex-1 py-3 bg-red-500 hover:bg-red-600 rounded-xl font-medium text-white flex items-center justify-center gap-2"
                            >
                                {cancelling ? (
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    'Sí, cancelar'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Onboarding Modal for First Time Users */}
            {showOnboarding && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-900 rounded-2xl p-6 w-full max-w-md border border-cyan-500/30 shadow-lg shadow-cyan-500/10">
                        {/* Step 1: Welcome and Question */}
                        {onboardingStep === 1 && (
                            <>
                                <div className="text-center mb-6">
                                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-500/20">
                                        <Calendar className="w-8 h-8 text-white" />
                                    </div>
                                    <h2 className="text-xl font-bold mb-2">Bienvenido a Asuntos Propios</h2>
                                    <p className="text-[var(--text-muted)] text-sm">
                                        Aquí podrás gestionar tus 6 días anuales de A.P.
                                    </p>
                                </div>

                                <div className="bg-white/5 rounded-xl p-4 mb-6">
                                    <p className="text-sm text-[var(--text-primary)] mb-4">
                                        <strong className="text-orange-500">¿Tienes días de arrastre de años anteriores?</strong>
                                    </p>
                                    <p className="text-xs text-[var(--text-secondary)]">
                                        El arrastre son días de A.P. que solicitaste pero la empresa te denegó,
                                        y que puedes usar este año.
                                    </p>
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        onClick={() => {
                                            setHasArrastreAnterior(false);
                                            handleSkipOnboarding();
                                        }}
                                        className="flex-1 py-3 rounded-xl border border-white/20 text-gray-300 hover:bg-white/5 transition-all"
                                    >
                                        No tengo
                                    </button>
                                    <button
                                        onClick={() => {
                                            setHasArrastreAnterior(true);
                                            setOnboardingStep(2);
                                        }}
                                        className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl font-bold text-white shadow-lg shadow-orange-500/20"
                                    >
                                        Sí, configurar
                                    </button>
                                </div>
                            </>
                        )}

                        {/* Step 2: Configure Arrastre */}
                        {onboardingStep === 2 && (
                            <>
                                <div className="flex items-center gap-3 mb-6">
                                    <button
                                        onClick={() => setOnboardingStep(1)}
                                        className="p-2 rounded-lg hover:bg-white/10"
                                    >
                                        <ChevronLeft className="w-5 h-5" />
                                    </button>
                                    <h2 className="font-semibold">Configurar Arrastre</h2>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm text-[var(--text-muted)] mb-2">
                                            ¿Cuántos días de arrastre tienes?
                                        </label>
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => setArrastreDias(Math.max(0, arrastreDias - 1))}
                                                className="w-12 h-12 rounded-xl bg-white/10 hover:bg-white/20 text-xl font-bold"
                                            >
                                                -
                                            </button>
                                            <div className="flex-1 text-center">
                                                <span className="text-4xl font-bold text-orange-500">{arrastreDias}</span>
                                                <p className="text-xs text-[var(--text-muted)] mt-1">días</p>
                                            </div>
                                            <button
                                                onClick={() => setArrastreDias(Math.min(10, arrastreDias + 1))}
                                                className="w-12 h-12 rounded-xl bg-white/10 hover:bg-white/20 text-xl font-bold"
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>

                                    {arrastreDias > 0 && (
                                        <>
                                            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 text-sm">
                                                <p className="text-orange-400 font-medium">
                                                    {arrastreDias} días → {
                                                        (() => {
                                                            let eq = 0;
                                                            for (let i = 1; i <= arrastreDias; i++) {
                                                                eq += (i >= 4) ? 2 : 1;
                                                            }
                                                            return eq;
                                                        })()
                                                    } días equivalentes
                                                </p>
                                                <p className="text-xs text-orange-400/70 mt-1">
                                                    (1-3 días = 1 c/u, 4º+ = 2 c/u)
                                                </p>
                                            </div>

                                            <div>
                                                <label className="block text-sm text-[var(--text-muted)] mb-1">
                                                    Notas (opcional)
                                                </label>
                                                <textarea
                                                    value={arrastreNotas}
                                                    onChange={(e) => setArrastreNotas(e.target.value)}
                                                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg h-20 resize-none text-sm"
                                                    placeholder="Ej: 2 días denegados en 2024..."
                                                />
                                            </div>
                                        </>
                                    )}

                                    <button
                                        onClick={handleOnboardingComplete}
                                        className="w-full py-3 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl font-bold text-white shadow-lg shadow-orange-500/20"
                                    >
                                        {arrastreDias > 0 ? 'Guardar y continuar' : 'Continuar sin arrastre'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Onboarding Tour */}
            <OnboardingTour
                steps={AP_TOUR_STEPS}
                onComplete={handleTourComplete}
                onSkip={handleTourSkip}
                isOpen={showTour}
                onStepChange={handleTourStepChange}
            />
            </div>
        </div>
    );
}
